// -- [Generic embed section - iframe or raw HTML, e.g. MongoDB Charts]

import type { EmbedConfig } from '@/types/content'

interface EmbedSectionProps {
  embeds: EmbedConfig[]
}

function EmbedBlock({ embed }: { embed: EmbedConfig }) {
  const isAspectClass = embed.aspectRatio?.startsWith('aspect-')
  const aspectClass = isAspectClass ? embed.aspectRatio : 'aspect-video'
  const aspectStyle =
    embed.aspectRatio && !isAspectClass
      ? { aspectRatio: embed.aspectRatio as React.CSSProperties['aspectRatio'] }
      : undefined

  if (embed.type === 'iframe' && embed.src) {
    return (
      <div
        className={`rounded-xl overflow-hidden shadow-sm bg-gray-100 ${aspectClass}`}
        style={aspectStyle}
      >
        <iframe
          src={embed.src}
          title={embed.title ?? 'Embedded content'}
          className="w-full h-full border-0"
          allowFullScreen
        />
      </div>
    )
  }

  if (embed.type === 'html' && embed.html) {
    return (
      <div
        className="rounded-xl overflow-hidden shadow-sm"
        dangerouslySetInnerHTML={{ __html: embed.html }}
      />
    )
  }

  return null
}

export function EmbedSection({ embeds }: EmbedSectionProps) {
  if (!embeds?.length) return null

  const validEmbeds = embeds.filter(
    (e) =>
      (e.type === 'iframe' && e.src?.trim()) || (e.type === 'html' && e.html?.trim())
  )
  if (validEmbeds.length === 0) return null

  return (
    <section id="embeds" className="py-16 px-6 bg-gray-50">
      <div className="max-w-6xl mx-auto space-y-12">
        {validEmbeds.map((embed, i) => (
          <div key={embed.id ?? `embed-${i}`}>
            {embed.title && (
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{embed.title}</h2>
            )}
            <EmbedBlock embed={embed} />
          </div>
        ))}
      </div>
    </section>
  )
}
